import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';

import SelectMenu from '../ui/SelectMenu';
import { useToast } from '../ui';
import PageHeader from '../ui/PageHeader';
import { settingsAPI } from '../../services/api';
import { useSettingsContext } from '../../hooks/useSettings';
import useTelemetry from '../../hooks/useTelemetry';

export default function QuoteDefaultsPage() {
  const [defaults, setDefaults] = useState({
    // IDI-205: account-wide labor burden %, applied on top of the total
    // labor bucket on every quote (see marginEngine.js costBreakdown).
    labor_burden_pct: '0',
    // Iter18 §2.5: closed set of 6 payment terms (OB-4).
    payment_terms: 'net_30',
    // Iter18 §2.5 Q4: single-select payment type default.
    payment_type: 'check',
    valid_until_days: '30',
    // Iter18 §2.5: rounding collapses to 2 options (final quote total only; line items never rounded).
    price_rounding: 'none',
    // Scheduling defaults (§0.5) — read by the WO range picker (§1.2)
    wo_default_start_time: '08:00',
    wo_default_end_time: '17:00',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const { emit } = useTelemetry();
  const toast = useToast();

  // PR-B2a (2026-07-21): Payment Terms + Payment Types dropdown options now sourced
  // from the canonical vocabSeeds via useSettings.getOptions, not from a hardcoded
  // array. This closes the last two shadow-default surfaces for these sets (both
  // are `custom: false` per operator ruling — tenants cannot introduce their own,
  // super-admin edits them via MasterDefaults). See plan `context/plans/tech-debt-cleanup-post-pr4.md` §2 PR-B2a.
  const { getOptions: getSettingsOptions } = useSettingsContext();
  const paymentTermsOptions = getSettingsOptions('payment_terms');
  const paymentTypesOptions = getSettingsOptions('payment_types');

  const fetchDefaults = useCallback(async () => {
    try {
      setLoading(true);
      const res = await settingsAPI.getQuoteDefaults();
      if (res) {
        setDefaults(prev => ({ ...prev, ...res }));
      }
    } catch (err) {
      setError('Failed to load quote defaults');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDefaults();
  }, [fetchDefaults]);

  const handleChange = (field, value) => {
    setDefaults(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await settingsAPI.updateQuoteDefaults(defaults);
      toast.success('Quote defaults saved');
      emit('settings.quote_defaults_updated', {});
    } catch (err) {
      setError('Failed to save quote defaults');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="space-y-3"><div className="h-8 rounded animate-pulse" style={{backgroundColor:"var(--neutral-100)"}} /><div className="h-32 rounded animate-pulse" style={{backgroundColor:"var(--neutral-100)"}} /></div>
      </div>
    );
  }

  return (
    <div className="px-6">
      <PageHeader
        title="Quote Defaults"
        subtitle="Set default values that apply to all new quotes."
      />

      {error && (
        <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <div className="p-6 space-y-6">
          {/* IDI-205: Labor Burden % — account-wide default, FIRST item on this
              page per stakeholder ruling (2026-08-28): "we can put that as, like,
              the first thing, which is the overall one, and then we can keep our
              other sections there, too." It sits above everything else because
              it's the account-wide multiplier the labor calculation on every
              quote feeds through — ordering here is semantic, not cosmetic.
              Calculation: labor_burden_amount = total_labor_bucket × (pct/100),
              applied to the TOTAL labor bucket (not per line item, not to
              material/other) — see quote-builder/marginEngine.js costBreakdown. */}
          <div>
            <label htmlFor="labor-burden-pct" className="block text-sm font-medium text-gray-700 mb-1">
              Labor Burden %
            </label>
            <div className="flex items-center gap-2">
              <Input
                id="labor-burden-pct"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={defaults.labor_burden_pct}
                onChange={(e) => handleChange('labor_burden_pct', e.target.value)}
                className="w-24"
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Applied on top of the total labor cost on every quote (e.g. 10% on a $1,000 labor bucket adds $100). Leave at 0 to apply no burden.
            </p>
          </div>

          {/* Payment Terms */}
          <div>
            <label htmlFor="payment-terms" className="block text-sm font-medium text-gray-700 mb-1">
              Default Payment Terms
            </label>
            <SelectMenu
              id="payment-terms"
              value={defaults.payment_terms}
              onChange={(val) => handleChange('payment_terms', val)}
              options={paymentTermsOptions}
              width="full"
              className="max-w-xs"
            />
            <p className="text-xs text-gray-500 mt-1">
              Auto-populated on new quotes. Can be overridden per quote.
            </p>
          </div>

          {/* Payment Type — Iter18 §2.5 Q4: single-select default per tenant. */}
          <div>
            <label htmlFor="payment-type" className="block text-sm font-medium text-gray-700 mb-1">
              Default Payment Type
            </label>
            <SelectMenu
              id="payment-type"
              value={defaults.payment_type}
              onChange={(val) => handleChange('payment_type', val)}
              options={paymentTypesOptions}
              width="full"
              className="max-w-xs"
            />
            <p className="text-xs text-gray-500 mt-1">
              Propagates to all downstream consumers of payment type.
            </p>
          </div>

          {/* Valid Until */}
          <div>
            <label htmlFor="valid-until" className="block text-sm font-medium text-gray-700 mb-1">
              Quote Valid For (days)
            </label>
            <div className="flex items-center gap-2">
              <Input
                id="valid-until"
                type="number"
                min="1"
                max="365"
                value={defaults.valid_until_days}
                onChange={(e) => handleChange('valid_until_days', e.target.value)}
                className="w-24"
              />
              <span className="text-sm text-gray-500">days from creation</span>
            </div>
          </div>

          {/* Price Rounding — Iter18 §2.5 collapsed to 2 options (OB-2 / OB-4).
              Applies to final quote total; line items never rounded. */}
          <div>
            <label htmlFor="price-rounding" className="block text-sm font-medium text-gray-700 mb-1">
              Price Rounding
            </label>
            <SelectMenu
              id="price-rounding"
              value={defaults.price_rounding === 'nearest_dollar' ? 'nearest_dollar' : 'none'}
              onChange={(val) => handleChange('price_rounding', val)}
              options={[
                { value: 'none', label: 'No rounding' },
                { value: 'nearest_dollar', label: 'Round to the nearest dollar' },
              ]}
              width="full"
              className="max-w-xs"
            />
            <p className="text-xs text-gray-500 mt-1">
              Applied to the final quote total. Line items are never rounded.
            </p>
          </div>

          {/* R20-11b: Scheduling Defaults section moved to Settings → Jobs & Work Orders.
              Data still lives under `quote_defaults` (data-shape unchanged);
              this page no longer offers a UI surface for those fields. */}

          {/* Save */}
          <div className="flex justify-end pt-4 border-t border-[color:var(--color-border)]">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
